'use no memo';
/**
 * Cross-Tab Auth Sync Hook
 *
 * Manages cross-tab authentication synchronization using BroadcastChannel.
 * Ensures only one tab refreshes tokens at a time and syncs auth state changes.
 *
 * AIDEV-NOTE: 'use no memo' directive - This hook intentionally mutates
 * lastTokenRef.current which is passed as a prop. This ref is shared with
 * useProactiveTokenRefresh for cross-hook token synchronization. The React Compiler
 * cannot optimize this pattern.
 */

import { useCallback, useEffect } from 'react';
import { CrossTabAuthSync } from '@utils/crossTabAuthSync';
import { signedOutByOtherTabSlot } from '@utils/crossTabAuthSync/authFlowFlags';
import { isNativePlatform } from '@lib/platform';
import { logger } from '@utils/logger';

// =============================================================================
// HOOK OPTIONS
// =============================================================================

export interface UseCrossTabAuthSyncOptions {
  /** Logto SDK getAccessToken function */
  getAccessToken: (resource?: string) => Promise<string | undefined>;
  /** Primary API resource for token requests */
  apiResource: string;
  /** Ref to check if user is authenticated (for filtering stale events) */
  logtoIsAuthenticatedRef: React.RefObject<boolean>;
  /** Shared ref for storing last token (used by proactive refresh too) */
  lastTokenRef: { current: string | null };
  /** Callback when another tab signs in */
  onSignedIn: () => void;
  /** Callback when another tab signs out */
  onSignedOut: () => void;
}

export interface UseCrossTabAuthSyncReturn {
  /** Get access token with cross-tab coordination to prevent refresh token conflicts */
  getAccessTokenWithCrossTabLock: (resource?: string) => Promise<string | null>;
}

// =============================================================================
// HOOK IMPLEMENTATION
// =============================================================================

/**
 * Manages cross-tab authentication synchronization.
 * Prevents "refresh token not found" errors when multiple tabs are open
 * by coordinating token refreshes via BroadcastChannel.
 */
export function useCrossTabAuthSync(
  options: UseCrossTabAuthSyncOptions,
): UseCrossTabAuthSyncReturn {
  const {
    getAccessToken,
    apiResource,
    logtoIsAuthenticatedRef,
    lastTokenRef,
    onSignedIn,
    onSignedOut,
  } = options;

  /**
   * AIDEV-NOTE: Wrapped getAccessToken with cross-tab coordination
   * Ensures only one tab refreshes at a time when refresh token rotation is enabled.
   *
   * On native: no cross-tab coordination needed (single WebView), so just passthrough.
   *
   * Cross-tab coordination is handled by CrossTabAuthSync.withRefreshLock which:
   * 1. Uses Web Locks API for mutual exclusion (only one tab holds lock at a time)
   * 2. Tracks via BroadcastChannel if another tab refreshed while we waited
   * 3. Calls onOtherTabRefreshed callback when that happens
   */
  const getAccessTokenWithCrossTabLock = useCallback(
    async (resource?: string): Promise<string | null> => {
      // Native: single WebView, no cross-tab coordination needed.
      // ?? null bridges the interface type (string | undefined) to return type (string | null);
      // the adapter guarantees string at runtime, so the ?? null is a type-level conversion only.
      if (isNativePlatform()) {
        const token = await getAccessToken(resource);
        return token ?? null;
      }

      const sync = CrossTabAuthSync.getInstance();

      if (sync.isOtherTabRefreshing()) {
        logger.debug('Cross-tab sync: Another tab is refreshing, waiting...');
      }

      return sync.withRefreshLock(
        async () => {
          const token = await getAccessToken(resource);
          return token ?? null;
        },
        async () => {
          logger.debug('Cross-tab sync: Using token refreshed by another tab');
          const token = await getAccessToken(resource);
          return token ?? null;
        },
      );
    },
    [getAccessToken],
  );

  /**
   * Initialize cross-tab sync listeners on mount.
   * No-op on native (single WebView, no cross-tab coordination needed).
   */
  useEffect(() => {
    if (isNativePlatform()) return;

    const sync = CrossTabAuthSync.getInstance();

    // Listen for token refreshes from other tabs
    const unsubTokenRefreshed = sync.onTokenRefreshed(() => {
      logger.debug('Cross-tab sync: Another tab refreshed tokens - syncing our state');
      lastTokenRef.current = null;

      // AIDEV-NOTE: When another tab refreshes tokens, we need to:
      // 1. Clear our cached token reference
      // 2. Re-fetch tokens to sync with localStorage (where fresh tokens are stored)
      // 3. Reschedule proactive refresh based on new token expiry
      // The getAccessToken call reads from shared localStorage, so no network request needed
      if (logtoIsAuthenticatedRef.current) {
        void getAccessToken(apiResource)
          .then(token => {
            if (token) {
              lastTokenRef.current = token;
              logger.debug('Cross-tab sync: Synced tokens from localStorage');
            }
          })
          .catch((err: unknown) => {
            logger.warn('Cross-tab sync: Failed to sync tokens', err);
          });
      }
    });

    // Listen for sign-in from other tabs
    // AIDEV-NOTE: Reload to pick up shared tokens from localStorage.
    // The Logto SDK will initialize with these tokens on reload.
    // Using reload() keeps the user on the same URL so they don't lose their place.
    const unsubSignedIn = sync.onSignedIn(() => {
      if (logtoIsAuthenticatedRef.current) {
        logger.debug('Cross-tab sync: Already authenticated, ignoring');
        return;
      }

      logger.info('Cross-tab sync: Another tab signed in - reloading to pick up shared tokens');
      onSignedIn();
    });

    // Listen for sign-out from other tabs
    const unsubSignedOut = sync.onSignedOut(() => {
      logger.info('Cross-tab sync: Another tab signed out - reloading to sync state');
      signedOutByOtherTabSlot.write(true);
      onSignedOut();
    });

    return () => {
      unsubTokenRefreshed();
      unsubSignedIn();
      unsubSignedOut();
    };
  }, [apiResource, getAccessToken, lastTokenRef, logtoIsAuthenticatedRef, onSignedIn, onSignedOut]);

  return {
    getAccessTokenWithCrossTabLock,
  };
}
